import { useFormatting } from "@msys/ui";
import { Stack } from "@mui/material";
import React from "react";
import {
  DecisionBehaviorOfSubitems,
  ItemType,
  QuantityUnit,
} from "../../../../clients/graphqlTypes.js";
import { durationInMinutesToTime } from "../../../utils.js";
import { useQuantityUnits } from "../../doc-items/useQuantityUnits.js";
import { DurationInlineEditor } from "./DurationInlineEditor.js";
import { InlineEditableCell } from "./InlineEditableCell.js";
import { QuantityInlineEditor } from "./QuantityInlineEditor.js";
import { QuantityUnitInlineEditor } from "./QuantityUnitInlineEditor.js";

type EditingTarget = "quantity" | "unit" | "time" | null;

interface Props {
  projectId: string | null;
  docId: string;
  itemId: string;
  itemType: ItemType;
  // The decision item's own selection mode — callers pass the item's
  // `decisionBehaviorOfSubitems` field (NOT `decisionOptionBehaviour`, which
  // is the parent's behaviour applied to decision *option* rows). For decision
  // items (`itemType === "decision"`): `SELECT_ONE` → render nothing (quantity
  // is implicit), `SELECT_MANY` → fall through to the regular logic (the outer
  // quantity multiplier is shown). For non-decision items, this prop is
  // ignored.
  decisionBehaviorOfSubitems: DecisionBehaviorOfSubitems;
  quantity: number;
  quantityUnit: QuantityUnit;
  // Work-duration in minutes for the time cell.
  timePerUnit: number;
  // The existing `estimatedQuantity` attribute-expression on the item, or
  // `undefined` if none. Threaded to the formula modal so it opens pre-filled
  // and lets the user edit / delete an existing formula — matching
  // `DataGridFloatWithExpressionDisplay` and `FormattedFloatWithExpressionField`.
  quantityExpression: string | undefined;
  // Whether the surrounding quote uses a time-and-material contract. When
  // true, the time cell is ALWAYS rendered on a second line (even when
  // `timePerUnit === 0`, so the user can enter the first duration). When
  // false, the time cell is never rendered.
  isTimeAndMaterialContract: boolean;
  onCommitQuantity: (value: number) => Promise<void>;
  onCommitUnit: (value: QuantityUnit) => Promise<void>;
  onCommitTime: (value: number) => Promise<void>;
  // Called after the quantity formula modal saves / deletes an expression, so
  // no-cache preview routes can refetch and reflect the change.
  onExpressionChange?: () => Promise<void> | void;
}

export function EditableQuantityCell({
  projectId,
  docId,
  itemId,
  itemType,
  decisionBehaviorOfSubitems,
  quantity,
  quantityUnit,
  timePerUnit,
  quantityExpression,
  isTimeAndMaterialContract,
  onCommitQuantity,
  onCommitUnit,
  onCommitTime,
  onExpressionChange,
}: Props) {
  const [editingTarget, setEditingTarget] = React.useState<EditingTarget>(null);
  const { quantityUnitLabels } = useQuantityUnits();
  const { getFormattedFloat } = useFormatting();

  // Decision items with SELECT_ONE behaviour: quantity is implicit (only one
  // option is picked), render nothing. SELECT_MANY decision items fall
  // through to the regular logic so the outer quantity multiplier is shown.
  if (itemType === "decision" && decisionBehaviorOfSubitems === "SELECT_ONE")
    return null;

  // Non-paid items (section, decision, …) with quantity === 1 (or undefined):
  // nothing to show, mirror today's behaviour.
  if (itemType !== "paid" && !(quantity > 1)) return null;

  const quantityCell = (
    <InlineEditableCell
      displayValue={getFormattedFloat(quantity ?? 0)}
      onEditStart={() => setEditingTarget("quantity")}
      onEditEnd={() => setEditingTarget(null)}
      renderEditor={handleBlur => (
        <QuantityInlineEditor
          projectId={projectId}
          docId={docId}
          itemId={itemId}
          initialQuantity={quantity ?? 0}
          expression={quantityExpression}
          onCommit={onCommitQuantity}
          onExpressionChange={onExpressionChange}
          handleBlur={handleBlur}
        />
      )}
    />
  );

  if (itemType !== "paid") {
    // Non-paid item (section, decision, …) with quantity > 1 → quantity only,
    // no unit, no time. Wrapped in a row Stack so the cell's `flex: 1` (active
    // during edit mode) has a flex parent to expand within and reaches the
    // full width.
    return (
      <Stack direction="row" sx={{ width: "100%" }}>
        {quantityCell}
      </Stack>
    );
  }

  const unitCell = (
    <InlineEditableCell
      displayValue={quantityUnitLabels[quantityUnit]}
      onEditStart={() => setEditingTarget("unit")}
      onEditEnd={() => setEditingTarget(null)}
      renderEditor={handleBlur => (
        <QuantityUnitInlineEditor
          initialUnit={quantityUnit}
          onCommit={onCommitUnit}
          handleBlur={handleBlur}
        />
      )}
    />
  );

  const timeCell = isTimeAndMaterialContract ? (
    <InlineEditableCell
      displayValue={`${durationInMinutesToTime(timePerUnit ?? 0)} h`}
      onEditStart={() => setEditingTarget("time")}
      onEditEnd={() => setEditingTarget(null)}
      renderEditor={handleBlur => (
        <DurationInlineEditor
          initialMinutes={timePerUnit ?? 0}
          onCommit={onCommitTime}
          handleBlur={handleBlur}
        />
      )}
    />
  ) : null;

  // Layout: quantity + unit on line 1; time on line 2.
  // When quantity is editing, unit is unmounted (and vice versa) so the
  // active editor can use the full width of the line. Time stays on its own
  // line regardless.
  return (
    <Stack direction="column" spacing={0} sx={{ width: "100%" }}>
      <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
        {editingTarget === "unit" ? null : quantityCell}
        {editingTarget === "quantity" ? null : unitCell}
      </Stack>
      {timeCell}
    </Stack>
  );
}
